import {
  BoxGeometry,
  CanvasTexture,
  Color,
  Group,
  Matrix4,
  Mesh,
  MeshBasicMaterial,
  Object3D,
  ObjectLoader,
  Quaternion,
  type Material,
  SphereGeometry,
  Sprite,
  SpriteMaterial,
  SRGBColorSpace,
  Vector3,
} from "three";
import { GLTFLoader } from "three/addons/loaders/GLTFLoader.js";
import { Ioc } from "@core/ioc/ioc";
import { FRAME_MODEL, type FrameModel } from "@core/frame/frame";
import { PICKING_SERVICE, type PickingService } from "@runtime/picking/pickingService";
import type { ThreeEngine } from "@runtime/engine/engineService";
import type {
  IMultiuserSessionModel,
  MultiuserParticipant,
} from "@models/multiuser/IMultiuserSessionModel";
import { participantAvatarPrefab, PRESENTER_TOOLS } from "@models/multiuser/IMultiuserSessionModel";
import type { IProjectModel } from "@models/project/IProjectModel";
import { createAvatarGlowMaterial, configureAvatarHeadShadowCast } from "./avatarGlowShader";
import { publicUrl } from "@core/publicUrl";
import {
  unityWorldPositionToThree,
  unityWorldQuaternionToThree,
} from "./participantTransformConversion";

/**
 * Renders remote session participants with the same representations the
 * Unity apps use (`MultiuserSessionModel.CreateNetworkUser` →
 * `NetworkUsersModel`):
 *   - headset wearers (Quest / desktop with HMD) → mannequin head
 *     (`Content/Gizmos/HeadPrefab` / `OculusPrefab`, `head13` mesh);
 *   - screen viewers (desktop without HMD / tablet) → floating device frame
 *     (`DisplayPrefab` / `TabletPrefab`, `ipadframe` mesh);
 *   - Campfire Remote → phone frame (`RemotePrefab`, `iphoneFrame` mesh).
 *
 * Meshes are the Campfire-Website exports of those same assets
 * (`public/avatars/head.glb`, `screen.json`, `remote.json`). Heads use the
 * website's fresnel glow shader (Unity `HeadMat` / `HeadShader`). Screen and
 * phone frames use a flat semi-transparent white material (Unity
 * `Phone Material` — FresnelPower 0, no rim fade). Identity color goes to the
 * name tag and laser only — never tints the head/frame mesh — matching Unity.
 *
 * Transforms arrive in Unity world space and convert at this boundary
 * (`participantTransformConversion.ts`) and are applied as SNAPS: Unity's
 * `NetworkUsersModel.SetPosition` / `SetToolPosition` always call the
 * interpolator with `bypassInterpolation: true` (`NetworkUsersModel.cs:334`,
 * `:399` — the calls commented "!interpolate" upstream), so remote motion
 * smoothness comes from the sender's per-frame sampling + 50 Hz throttle,
 * not from receiver
 * easing. The `TransformInterpolator` port below (3 m/s, min 0.4 s) is kept
 * dormant like Unity's — re-easing received poses here re-introduces a
 * ≥400 ms visual lag on every update (the pre-2026-08 sluggish-avatar bug).
 */

const HEAD_URL = publicUrl("avatars/head.glb");
const SCREEN_URL = publicUrl("avatars/screen.json");
const PHONE_URL = publicUrl("avatars/remote.json");

// Unity `TransformInterpolator` constants. Dormant on the network-pose path
// (see the header note) — they only run if a caller ever passes
// `bypassInterpolation: false` to `setTarget`, mirroring Unity's dormant
// interpolator machinery.
const INTERPOLATION_SPEED_M_PER_S = 3.0;
const INTERPOLATION_MIN_DURATION_S = 0.4;

// Unity prefab locals, hand-converted into the avatar group's THREE-local frame.
//
// That frame changed with task 0004 and these constants moved with it. The
// group's rotation now comes from `convertWorldPoseQuaternion`, so a Unity local
// offset `u` lands at `diag(1, 1, -1) · u` — where it previously landed at
// `diag(-1, 1, -1) · u`. Net effect on every constant below: the X sign flips.
//
// What that fixed, concretely: the old frame sent Unity's +X ("user right") to
// three's -X, so remote avatars and their attachments were LEFT-RIGHT MIRRORED
// about their own pivot. The head offset is 1 mm and was undetectable; the phone
// offset is 2 cm and was not.
//
// The frame is now a proper right-handed one — forward -Z, up +Y, right +X — so
// re-derive from the prefab by negating Z only.

// From `HeadPrefab`: UserInstance z=0.083 + AudienceHead (0.001, -0.053, -0.021),
// i.e. Unity local (0.001, -0.053, 0.062).
const HEAD_LOCAL_POSITION: readonly [number, number, number] = [0.001, -0.053, -0.062];
const HEAD_LOCAL_SCALE = 0.038;
// head13 geometry faces +X as loaded by GLTFLoader; +90° yaw turns it to the
// group's -Z "user forward" (same correction the website client applies).
// UNCHANGED by the basis fix: this is a three-native correction on raw glTF
// geometry, not a converted Unity value, and "forward is local -Z" is exactly
// the invariant `convertWorldPoseQuaternion` preserves.
const HEAD_LOCAL_YAW_RAD = Math.PI / 2;
// From `RemotePrefab`: UserInstance (-0.02, 0, -0.04) rotated -60° about Y.
const PHONE_LOCAL_POSITION: readonly [number, number, number] = [-0.02, 0, 0.04];
// Ry(-60) in Unity. The frame change `diag(1, 1, -1)` is a REFLECTION, so a
// rotation about the (invariant) Y axis reverses sense: Ry(-60) → Ry(+60).
const PHONE_LOCAL_QUATERNION: readonly [number, number, number, number] = [0, 0.5, 0, 0.8660254];

const REPRESENTATION_RENDER_ORDER = 990;
const NAME_TAG_RENDER_ORDER = 992;
// UserTag anchors 0.15 below the user instance in Unity's presence prefabs;
// nudged slightly lower so the pill clears the mannequin chin.
const NAME_TAG_OFFSET_Y = -0.2;
const NAME_TAG_HEIGHT_M = 0.055;
const LASER_THICKNESS_M = 0.004;
/** Scratch for orienting a peer's laser in its PARENT's frame — see `placeToolBeam`. */
const SCRATCH_LOOK = new Matrix4();
const LASER_UP = new Vector3(0, 1, 0);
const LASER_POINT_RADIUS_M = 0.006;
const FALLBACK_CIRCLE_RADIUS_M = 1.5;
const FALLBACK_HEIGHT_M = 1.2;

type AvatarRepresentation = "head" | "screen" | "phone";

interface AvatarRecord {
  readonly key: string;
  representation: AvatarRepresentation;
  readonly root: Group;
  visual: Object3D | null;
  nameTag: Sprite | null;
  nameTagKey: string;
  readonly beam: Mesh;
  readonly beamMaterial: MeshBasicMaterial;
  readonly laserPoint: Mesh;
  /**
   * AI-1180 (`UserInstanceBaseBehaviour.IsFollowedByCurrentUser`): the avatar
   * the local user is following is hidden from their own view — the camera
   * sits inside it. Covers the representation, name tag, and laser.
   */
  hidden: boolean;
  /** Project `HideAvatars` applied to the representation mesh (AI-925). */
  representationHidden: boolean;
  /** Project `HideUserTags` applied to the name-tag billboard (AI-925). */
  tagHidden: boolean;
  toolTarget: Vector3 | null;
  toolVisible: boolean;
  // TransformInterpolator state (Three world space).
  hasPose: boolean;
  readonly startPosition: Vector3;
  readonly startRotation: Quaternion;
  readonly endPosition: Vector3;
  readonly endRotation: Quaternion;
  t: number;
}

interface AvatarTemplates {
  head: Object3D | null;
  screen: Object3D | null;
  phone: Object3D | null;
}

export class NetworkUsersRenderer {
  private readonly m_root = new Group();
  private readonly m_avatars = new Map<string, AvatarRecord>();
  private readonly m_glowMaterial = createAvatarGlowMaterial("participant");
  private readonly m_screenMaterial = createAvatarScreenMaterial();
  private readonly m_beamGeometry = new BoxGeometry(1, 1, 1);
  private readonly m_laserPointGeometry = new SphereGeometry(LASER_POINT_RADIUS_M, 16, 12);
  private readonly m_laserPointMaterial = new MeshBasicMaterial({ color: "#ffffff" });
  private readonly m_templates: AvatarTemplates = { head: null, screen: null, phone: null };
  private readonly m_offFns: Array<() => void> = [];
  private m_templatesReady = false;
  private m_disposed = false;

  constructor(
    private readonly m_engine: ThreeEngine,
    private readonly m_multiuser: IMultiuserSessionModel,
    private readonly m_project: IProjectModel,
  ) {
    this.m_root.name = "multiuserRoot";
  }

  mount(): void {
    this.m_engine.root.add(this.m_root);
    if (Ioc.isRegistered<PickingService>(PICKING_SERVICE)) {
      // Presence gizmos must never steal viewport picks from scene content.
      Ioc.resolve<PickingService>(PICKING_SERVICE).markUnpickable(this.m_root);
    }
    this.m_offFns.push(
      this.m_multiuser.onParticipantsChanged.on(() => this.syncActiveProject()),
      // The LOCAL user's transform changes at up to 50 Hz during camera
      // motion and never affects a remote avatar (sync filters local out) —
      // skip it instead of re-walking the roster per emit.
      this.m_multiuser.onParticipantTransformChanged.on((participant) => {
        if (!participant.isLocal) this.syncActiveProject();
      }),
      // Follow start/stop re-evaluates the followed avatar's hidden state.
      this.m_multiuser.onLocalFollowTargetChanged.on(() => this.syncActiveProject()),
      // AI-925: a peer (or the local settings panel) flipped HideAvatars /
      // HideUserTags — re-evaluate every avatar live, like Unity's
      // `ProjectUserDisplaySettingsChanged` subscription.
      this.m_project.onActiveProjectInfoChanged.on(() => this.syncActiveProject()),
      // A project SWITCH changes which participants belong here — without
      // this, the avatars of the project just left would linger until some
      // other event happened to re-sync.
      this.m_project.onActiveProjectChanged.on(() => this.syncActiveProject()),
      Ioc.resolve<FrameModel>(FRAME_MODEL).onTick.on(({ deltaSeconds }) => this.tick(deltaSeconds)),
    );
    void this.loadTemplatesAsync();
    this.syncActiveProject();
  }

  /**
   * Avatars are for the participants in THIS project — Unity builds the same
   * set from `GetSessionParticipants(m_projectID, m_accountId, excludeRemote:
   * false)` (`MultiuserSessionModel.cs:349`).
   *
   * The roster itself is account-wide (the event relay is), so reading it raw
   * here put an avatar in the scene for every user connected to the account,
   * whatever project they were in. `excludeRemote: false` because a Campfire
   * Remote peer IS rendered — as the phone frame — even though the roster
   * list leaves them out.
   */
  private syncActiveProject(): void {
    this.sync(this.m_multiuser.getActiveProjectParticipants({ excludeRemote: false }));
  }

  dispose(): void {
    this.m_disposed = true;
    for (const off of this.m_offFns) off();
    this.m_offFns.length = 0;
    for (const avatar of this.m_avatars.values()) this.removeAvatar(avatar);
    this.m_avatars.clear();
    for (const template of Object.values(this.m_templates)) {
      if (template) disposeGeometries(template);
    }
    this.m_glowMaterial.dispose();
    this.m_screenMaterial.dispose();
    this.m_beamGeometry.dispose();
    this.m_laserPointGeometry.dispose();
    this.m_laserPointMaterial.dispose();
    this.m_root.removeFromParent();
  }

  private async loadTemplatesAsync(): Promise<void> {
    const gltfLoader = new GLTFLoader();
    const objectLoader = new ObjectLoader();
    const [head, screen, phone] = await Promise.all([
      gltfLoader.loadAsync(HEAD_URL).then((gltf) => gltf.scene).catch((err: unknown) => {
        console.warn("[NetworkUsersRenderer] failed to load head avatar", err);
        return null;
      }),
      objectLoader.loadAsync(SCREEN_URL).catch((err: unknown) => {
        console.warn("[NetworkUsersRenderer] failed to load screen avatar", err);
        return null;
      }),
      objectLoader.loadAsync(PHONE_URL).catch((err: unknown) => {
        console.warn("[NetworkUsersRenderer] failed to load phone avatar", err);
        return null;
      }),
    ]);
    if (this.m_disposed) return;

    if (head) {
      const wrapper = new Group();
      wrapper.name = "avatar.head";
      head.position.set(...HEAD_LOCAL_POSITION);
      head.rotation.y = HEAD_LOCAL_YAW_RAD;
      head.scale.setScalar(HEAD_LOCAL_SCALE);
      wrapper.add(head);
      this.m_templates.head = this.prepareTemplate(wrapper, this.m_glowMaterial, true);
    }
    if (screen) {
      screen.name = "avatar.screen";
      this.m_templates.screen = this.prepareTemplate(screen, this.m_screenMaterial);
    }
    if (phone) {
      const wrapper = new Group();
      wrapper.name = "avatar.phone";
      // Strip the website's baked hover offset; RemotePrefab's local pose wins.
      phone.position.set(0, 0, 0);
      wrapper.add(phone);
      wrapper.position.set(...PHONE_LOCAL_POSITION);
      wrapper.quaternion.set(...PHONE_LOCAL_QUATERNION);
      this.m_templates.phone = this.prepareTemplate(wrapper, this.m_screenMaterial);
    }
    this.m_templatesReady = true;

    // Attach representations to any avatars that arrived before the meshes.
    for (const avatar of this.m_avatars.values()) {
      this.attachRepresentation(avatar);
    }
    this.m_engine.requestRender();
  }

  private prepareTemplate(root: Object3D, material: Material, castShadow = false): Object3D {
    root.traverse((node) => {
      if ((node as Mesh).isMesh) {
        const mesh = node as Mesh;
        mesh.material = material;
        mesh.renderOrder = REPRESENTATION_RENDER_ORDER;
        if (castShadow) {
          configureAvatarHeadShadowCast(mesh);
        } else {
          mesh.castShadow = false;
          mesh.receiveShadow = false;
          mesh.customDepthMaterial = undefined;
        }
      }
    });
    return root;
  }

  private sync(participants: ReadonlyArray<MultiuserParticipant>): void {
    if (this.m_disposed) return;
    // Render on demand: poses snap here (no interpolation runs), as do
    // joins, leaves, name tags and lasers — and the end of the template load.
    this.m_engine.requestRender();
    const remote = participants.filter((p) => !p.isLocal && p.isOnline);
    const wanted = new Set(remote.map((p) => p.key));
    for (const [key, avatar] of this.m_avatars) {
      if (wanted.has(key)) continue;
      this.removeAvatar(avatar);
      this.m_avatars.delete(key);
    }
    remote.forEach((participant, index) => {
      this.syncParticipant(participant, index, remote.length);
    });
  }

  private syncParticipant(
    participant: MultiuserParticipant,
    index: number,
    count: number,
  ): void {
    const avatar = this.ensureAvatar(participant);
    // Visibility precedence, port of `UserInstanceBaseBehaviour.IsVisible` /
    // `IsUserTagVisible`:
    //   1. followed by the local user → everything hidden (AI-1180), wins;
    //   2. wielding a LASER → representation AND tag forced visible even
    //      under the project flags (`IsLaserVisibilityOverrideActive`;
    //      Annotation / Grab / others do NOT override);
    //   3. project `HideAvatars` hides the representation mesh and
    //      `HideUserTags` hides the name tag — INDEPENDENT gates (Unity's
    //      tag getter reads the raw tracked visibility, so HideAvatars alone
    //      never hides the tag).
    const project = this.m_project.activeProject;
    const laserOverride = hasLaserVisual(participant.activeTool);
    avatar.hidden = participant.key === this.m_multiuser.localFollowedKey;
    avatar.root.visible = !avatar.hidden;
    avatar.representationHidden = !laserOverride && project?.hideAvatars === true;
    avatar.tagHidden = !laserOverride && project?.hideUserTags === true;
    if (avatar.visual) avatar.visual.visible = !avatar.representationHidden;
    this.updateNameTag(avatar, participant);
    if (avatar.nameTag) avatar.nameTag.visible = !avatar.tagHidden;
    avatar.beamMaterial.color = colorFrom(participant.color);

    const transform = participant.transform;
    if (transform) {
      // Unity applies network poses with `bypassInterpolation: true`
      // (`NetworkUsersModel.cs:334`, commented "!interpolate" upstream):
      // the avatar snaps to each received pose. Senders stream per-frame
      // at up to 50 Hz, so easing here only adds lag.
      this.setTarget(
        avatar,
        unityWorldPositionToThree(transform.position),
        unityWorldQuaternionToThree(transform.rotation),
        true,
      );
      avatar.toolTarget = transform.toolPosition
        ? unityWorldPositionToThree(transform.toolPosition)
        : null;
      // Unity per-tool getters are `flag && IsToolVisible`, so the beam needs
      // BOTH a beam-rendering tool and a non-zero visibility mask. Web
      // deviation: the Annotation tool reuses the laser beam rather than
      // Unity's separate straight-laser child.
      avatar.toolVisible =
        (transform.toolVisibility ?? 0) !== 0 && rendersToolBeam(participant.activeTool);
    } else if (!avatar.hasPose) {
      // No pose yet (e.g. roster entry from shared state without a
      // transform): park the avatar on a circle around the table.
      const angle = (Math.PI * 2 * index) / Math.max(1, count);
      const position = new Vector3(
        Math.cos(angle) * FALLBACK_CIRCLE_RADIUS_M,
        FALLBACK_HEIGHT_M,
        Math.sin(angle) * FALLBACK_CIRCLE_RADIUS_M,
      );
      // Face the origin: local -Z (user forward) points at the table center.
      const facing = new Quaternion().setFromAxisAngle(
        new Vector3(0, 1, 0),
        Math.atan2(position.x, position.z),
      );
      this.setTarget(avatar, position, facing, true);
    }
    this.placeToolBeam(avatar);
  }

  private ensureAvatar(participant: MultiuserParticipant): AvatarRecord {
    const representation = representationFor(participant);
    const existing = this.m_avatars.get(participant.key);
    if (existing) {
      if (existing.representation !== representation) {
        existing.representation = representation;
        this.attachRepresentation(existing);
      }
      return existing;
    }

    const root = new Group();
    root.name = `participant:${participant.key}`;
    this.m_root.add(root);

    const beamMaterial = new MeshBasicMaterial({
      color: colorFrom(participant.color),
      transparent: true,
      opacity: 0.9,
      depthWrite: false,
    });
    const beam = new Mesh(this.m_beamGeometry, beamMaterial);
    beam.name = `participant:${participant.key}:laser`;
    beam.visible = false;
    this.m_root.add(beam);

    const laserPoint = new Mesh(this.m_laserPointGeometry, this.m_laserPointMaterial);
    laserPoint.name = `participant:${participant.key}:laserPoint`;
    laserPoint.visible = false;
    this.m_root.add(laserPoint);

    const avatar: AvatarRecord = {
      key: participant.key,
      representation,
      root,
      visual: null,
      nameTag: null,
      nameTagKey: "",
      beam,
      beamMaterial,
      laserPoint,
      hidden: false,
      representationHidden: false,
      tagHidden: false,
      toolTarget: null,
      toolVisible: false,
      hasPose: false,
      startPosition: new Vector3(),
      startRotation: new Quaternion(),
      endPosition: new Vector3(),
      endRotation: new Quaternion(),
      t: 2,
    };
    this.m_avatars.set(participant.key, avatar);
    this.attachRepresentation(avatar);
    return avatar;
  }

  private attachRepresentation(avatar: AvatarRecord): void {
    if (avatar.visual) {
      avatar.visual.removeFromParent();
      avatar.visual = null;
    }
    if (!this.m_templatesReady) return;
    const template = this.m_templates[avatar.representation];
    if (!template) return;
    const visual = template.clone(true);
    visual.visible = !avatar.representationHidden;
    avatar.root.add(visual);
    avatar.visual = visual;
  }

  private updateNameTag(avatar: AvatarRecord, participant: MultiuserParticipant): void {
    const key = `${participant.displayName}|${participant.initials}|${participant.color}|${participant.isPresenter ? "P" : ""}`;
    if (avatar.nameTagKey === key) return;
    avatar.nameTagKey = key;
    if (avatar.nameTag) {
      disposeNameTag(avatar.nameTag);
      avatar.nameTag.removeFromParent();
    }
    avatar.nameTag = createNameTag(
      participant.displayName,
      participant.initials,
      participant.color,
      null,
      participant.isPresenter,
    );
    avatar.nameTag.position.set(0, NAME_TAG_OFFSET_Y, 0);
    avatar.nameTag.visible = !avatar.tagHidden;
    avatar.root.add(avatar.nameTag);
  }

  /** Port of Unity's `TransformInterpolator.SetTargetTransform`. */
  private setTarget(
    avatar: AvatarRecord,
    position: Vector3,
    rotation: Quaternion,
    bypassInterpolation = false,
  ): void {
    if (bypassInterpolation || !avatar.hasPose) {
      avatar.root.position.copy(position);
      avatar.root.quaternion.copy(rotation);
      avatar.startPosition.copy(position);
      avatar.startRotation.copy(rotation);
      avatar.endPosition.copy(position);
      avatar.endRotation.copy(rotation);
      avatar.t = 2;
      avatar.hasPose = true;
      return;
    }
    avatar.startPosition.copy(avatar.root.position);
    avatar.startRotation.copy(avatar.root.quaternion);
    avatar.endPosition.copy(position);
    avatar.endRotation.copy(rotation);
    avatar.t = 0;
  }

  /** Port of Unity's `TransformInterpolator.Update`. */
  private tick(deltaSeconds: number): void {
    for (const avatar of this.m_avatars.values()) {
      if (!avatar.hasPose) continue;
      if (avatar.t <= 1) {
        const distance = avatar.startPosition.distanceTo(avatar.endPosition);
        const duration = Math.max(
          INTERPOLATION_MIN_DURATION_S,
          distance / INTERPOLATION_SPEED_M_PER_S,
        );
        avatar.t += deltaSeconds / duration;
        const k = Math.min(1, avatar.t);
        avatar.root.position.lerpVectors(avatar.startPosition, avatar.endPosition, k);
        avatar.root.quaternion.slerpQuaternions(avatar.startRotation, avatar.endRotation, k);
        this.placeToolBeam(avatar);
      }
    }
  }

  private placeToolBeam(avatar: AvatarRecord): void {
    const target = avatar.toolTarget;
    if (avatar.hidden || !target || !avatar.toolVisible || !avatar.hasPose) {
      avatar.beam.visible = false;
      avatar.laserPoint.visible = false;
      return;
    }
    const start = avatar.root.position;
    const length = start.distanceTo(target);
    if (length < 0.01) {
      avatar.beam.visible = false;
      avatar.laserPoint.visible = false;
      return;
    }
    avatar.beam.visible = true;
    avatar.beam.position.copy(start).add(target).multiplyScalar(0.5);
    // `start` and `target` are STAGE-local (both live under `multiuserRoot`,
    // itself under `engine.root`), but `Object3D.lookAt` takes a WORLD point —
    // so orient in the parent's own frame instead. Identical while the stage
    // is at the origin; console placement moves it, and a world `lookAt` would
    // then skew every peer's laser by the stage's yaw.
    SCRATCH_LOOK.lookAt(avatar.beam.position, target, LASER_UP);
    avatar.beam.quaternion.setFromRotationMatrix(SCRATCH_LOOK);
    avatar.beam.scale.set(LASER_THICKNESS_M, LASER_THICKNESS_M, length);
    avatar.laserPoint.visible = true;
    avatar.laserPoint.position.copy(target);
  }

  private removeAvatar(avatar: AvatarRecord): void {
    if (avatar.nameTag) disposeNameTag(avatar.nameTag);
    avatar.beamMaterial.dispose();
    avatar.beam.removeFromParent();
    avatar.laserPoint.removeFromParent();
    avatar.root.removeFromParent();
  }
}

/**
 * The visual for the prefab Unity would have spawned — the decision table
 * itself lives in `participantAvatarPrefab` (shared with the follow gate, so
 * the two can never disagree about what a peer is). Tablet and display both
 * draw the screen frame here; Unity's separate `TabletBehaviour` prefab has
 * no distinct web visual yet.
 */
function representationFor(participant: MultiuserParticipant): AvatarRepresentation {
  switch (participantAvatarPrefab(participant)) {
    case "quest":
    case "head":
      return "head";
    case "remote":
      return "phone";
    case "tablet":
    case "display":
    default:
      return "screen";
  }
}

/**
 * Unity `UserInstanceBaseBehaviour.handlePresenterToolChanged`: which
 * `PresenterTools` flags render the LASER visual (and therefore force the
 * avatar + tag visible via `IsLaserVisibilityOverrideActive`). BoundingBox
 * and Scale map to the laser look upstream too.
 */
function hasLaserVisual(activeTool: number): boolean {
  return (
    activeTool === PRESENTER_TOOLS.LaserTool
    || activeTool === PRESENTER_TOOLS.ScaleTool
    || activeTool === PRESENTER_TOOLS.BoundingBoxTool
  );
}

/**
 * Whether the tool state renders a beam at all. Web deviation: Unity gives
 * the Annotation tool its own straight-laser child; the web reuses the same
 * beam mesh. FlashLight / RotateMove / Sketch / Repos render nothing.
 */
function rendersToolBeam(activeTool: number): boolean {
  return hasLaserVisual(activeTool) || activeTool === PRESENTER_TOOLS.AnnotationTool;
}

function colorFrom(value: string): Color {
  try {
    return new Color(value);
  } catch {
    return new Color().setRGB(0.15, 0.75, 1, SRGBColorSpace);
  }
}

/** Flat semi-transparent white for desktop/tablet/phone frames — ports Unity's
 *  `Phone Material` (FresnelPower 0, uniform alpha, no rim fade). */
function createAvatarScreenMaterial(): MeshBasicMaterial {
  const material = new MeshBasicMaterial({
    color: 0xffffff,
    transparent: true,
    opacity: 0.12,
    depthWrite: false,
  });
  material.name = "avatar.screen";
  return material;
}

/**
 * Billboard name tag: black pill, user-color border, initials disc + name.
 * Web equivalent of Unity's `Content/Controls/UserTag.prefab` billboard.
 * Shared with Spark (`AI/Spark/NameTag.prefab` reuses the same layout with
 * a picture in place of the initials disc — pass `picture`).
 */
export function createNameTag(
  displayName: string,
  initials: string,
  color: string,
  picture?: HTMLImageElement | null,
  isPresenter = false,
): Sprite {
  const height = 128;
  const padding = 28;
  const discRadius = 40;
  const fontPx = 52;
  const border = 6;

  const measure = document.createElement("canvas").getContext("2d");
  let textWidth = 200;
  if (measure) {
    measure.font = `600 ${fontPx}px -apple-system, "Segoe UI", Roboto, sans-serif`;
    textWidth = Math.ceil(measure.measureText(displayName).width);
  }
  const width = padding + discRadius * 2 + 20 + textWidth + padding;

  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext("2d");
  const sprite = new Sprite();
  if (ctx) {
    const radius = height / 2;
    ctx.beginPath();
    ctx.roundRect(border / 2, border / 2, width - border, height - border, radius - border / 2);
    ctx.fillStyle = "rgba(10, 10, 12, 0.92)";
    ctx.fill();
    ctx.lineWidth = border;
    ctx.strokeStyle = color;
    ctx.stroke();

    if (picture) {
      // Picture disc (Spark's `SparkPic` RawImage) instead of initials.
      ctx.save();
      ctx.beginPath();
      ctx.arc(padding + discRadius, height / 2, discRadius, 0, Math.PI * 2);
      ctx.clip();
      ctx.drawImage(
        picture,
        padding,
        height / 2 - discRadius,
        discRadius * 2,
        discRadius * 2,
      );
      ctx.restore();
    } else {
      ctx.beginPath();
      ctx.arc(padding + discRadius, height / 2, discRadius, 0, Math.PI * 2);
      ctx.fillStyle = color;
      ctx.fill();

      ctx.font = `700 ${Math.round(fontPx * 0.62)}px -apple-system, "Segoe UI", Roboto, sans-serif`;
      ctx.fillStyle = "#0a0a0c";
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.fillText(initials, padding + discRadius, height / 2 + 2);
    }

    ctx.font = `600 ${fontPx}px -apple-system, "Segoe UI", Roboto, sans-serif`;
    ctx.fillStyle = "#ffffff";
    ctx.textAlign = "left";
    ctx.textBaseline = "middle";
    ctx.fillText(displayName, padding + discRadius * 2 + 20, height / 2 + 2);

    if (isPresenter) {
      // Presenter badge — Unity's plain yellow (#FFE600) filled dot
      // (`ParticipantListItemView.prefab`; `UserTagItemViewModel.IsPresenter`),
      // rendered on the initials disc's upper-right rim.
      const dotRadius = 9;
      const dotX = padding + discRadius + discRadius * 0.7;
      const dotY = height / 2 - discRadius * 0.7;
      ctx.beginPath();
      ctx.arc(dotX, dotY, dotRadius, 0, Math.PI * 2);
      ctx.fillStyle = "#FFE600";
      ctx.fill();
      ctx.lineWidth = 3;
      ctx.strokeStyle = "rgba(10, 10, 12, 0.92)";
      ctx.stroke();
    }
  }

  const texture = new CanvasTexture(canvas);
  texture.colorSpace = SRGBColorSpace;
  sprite.material = new SpriteMaterial({
    map: texture,
    transparent: true,
    depthTest: false,
  });
  sprite.renderOrder = NAME_TAG_RENDER_ORDER;
  sprite.scale.set(NAME_TAG_HEIGHT_M * (width / height), NAME_TAG_HEIGHT_M, 1);
  return sprite;
}

export function disposeNameTag(tag: Sprite): void {
  tag.material.map?.dispose();
  tag.material.dispose();
}

function disposeGeometries(root: Object3D): void {
  root.traverse((node) => {
    if ((node as Mesh).isMesh) (node as Mesh).geometry.dispose();
  });
}
